import { Button } from '@/components/ui/button';
import type { ICardFillAdminJob } from '@/entities/card-fill-admin.entity';
import {
	CARD_FILL_JOB_STATUS_LABELS,
	CARD_FILL_JOB_TYPE_LABELS,
	formatDateTime,
	formatDuration,
	formatJobResultDetails,
	getJobOutcome,
	getJobOutcomeLabel,
	jobDurationMs,
	readJobPayloadString,
	summarizeJobResult,
	type CardFillJobOutcome,
} from '@/lib/card-fill-meta';
import { cn } from '@/lib/utils';
import CardFillMetaRow from '@/pages/admin/card-fill/card-fill-meta-row';
import { formatLeaseRemaining } from '@/pages/admin/card-fill/format';
import { buildClientPath, buildClientRouteState } from '@/lib/client-navigation';
import { Link, useLocation } from 'react-router';

function outcomeBorderClass(outcome: CardFillJobOutcome): string {
	switch (outcome) {
		case 'success':
			return 'border-l-chart-2';
		case 'partial':
			return 'border-l-amber-500';
		case 'empty':
			return 'border-l-amber-500/70';
		case 'failed':
			return 'border-l-destructive';
		case 'cancelled':
			return 'border-l-muted-foreground/50';
		default:
			return 'border-l-chart-3';
	}
}

function outcomeBadgeClass(outcome: CardFillJobOutcome): string {
	switch (outcome) {
		case 'success':
			return 'text-chart-2 border-chart-2/40 bg-chart-2/10';
		case 'partial':
			return 'text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/10';
		case 'empty':
			return 'text-amber-700 dark:text-amber-400 border-amber-500/30 bg-amber-500/10';
		case 'failed':
			return 'text-destructive border-destructive/40 bg-destructive/10';
		case 'cancelled':
			return 'text-muted-foreground border-border bg-muted/30';
		default:
			return 'text-chart-3 border-chart-3/40 bg-chart-3/10';
	}
}

function resolveJobMask(job: ICardFillAdminJob): string | null {
	return (
		job.cardMask?.trim() ||
		readJobPayloadString(job.payload, 'mask') ||
		readJobPayloadString(job.payload, 'pan') ||
		null
	);
}

export default function CardFillJobRow({
	job,
	onCancel,
	onRetry,
}: {
	job: ICardFillAdminJob;
	onCancel?: (jobId: string) => void;
	onRetry?: (jobId: string) => void;
}) {
	const location = useLocation();
	const clientLinkState = buildClientRouteState(location.pathname, location.search);
	const outcome = getJobOutcome(job);
	const lease = formatLeaseRemaining(job.leaseExpiresAt);
	const staleLease = job.status === 'running' && lease === 'reclaim ожидается';
	const duration = jobDurationMs(job);
	const summary = summarizeJobResult(job);
	const details = formatJobResultDetails(job);
	const mask = resolveJobMask(job);
	const expectedOwner = readJobPayloadString(job.payload, 'expectedOwner');
	const typeLabel = CARD_FILL_JOB_TYPE_LABELS[job.jobType] ?? job.jobType;
	const statusLabel = CARD_FILL_JOB_STATUS_LABELS[job.status] ?? job.status;
	const outcomeLabel = getJobOutcomeLabel(job);
	const showOutcomeBadge = job.status === 'done' || job.status === 'failed' || job.status === 'cancelled';

	return (
		<div
			className={cn(
				'rounded-xl border border-border bg-card p-3 shadow-sm border-l-[3px] space-y-2.5',
				outcomeBorderClass(outcome),
			)}
		>
			<div className='flex items-start justify-between gap-3'>
				<div className='min-w-0 space-y-2 flex-1'>
					<div className='flex flex-wrap items-center gap-2'>
						<span className='font-medium text-sm'>{typeLabel}</span>
						<span className='text-xs rounded-full border px-2 py-0.5 text-muted-foreground border-border'>
							{statusLabel}
						</span>
						{showOutcomeBadge ? (
							<span className={cn('text-xs rounded-full border px-2 py-0.5', outcomeBadgeClass(outcome))}>
								{outcomeLabel}
							</span>
						) : null}
						{staleLease ? (
							<span className='text-xs rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 px-2 py-0.5'>
								{lease}
							</span>
						) : lease ? (
							<span className='text-xs text-muted-foreground'>lease {lease}</span>
						) : null}
					</div>

					<div className='grid gap-1.5 sm:grid-cols-2'>
						<CardFillMetaRow label='Профиль' value={job.profileNickname ? `@${job.profileNickname}` : null} />
						<CardFillMetaRow label='Телефон' value={job.phoneNumber} />
						<CardFillMetaRow label='Маска / PAN' value={mask} />
						<CardFillMetaRow label='Ожид. ФИО' value={expectedOwner} />
					</div>

					<Link
						to={buildClientPath(job.clientId)}
						state={clientLinkState}
						className='text-xs text-chart-2 hover:underline inline-block'
					>
						Открыть клиента
					</Link>
				</div>

				<div className='flex flex-col gap-1 shrink-0'>
					{job.status === 'pending' && onCancel ? (
						<Button type='button' size='sm' variant='outline' onClick={() => onCancel(job.id)}>
							Отменить
						</Button>
					) : null}
					{(job.status === 'failed' || job.status === 'cancelled') && onRetry ? (
						<Button type='button' size='sm' variant='secondary' onClick={() => onRetry(job.id)}>
							Повторить
						</Button>
					) : null}
				</div>
			</div>

			{summary ? (
				<p
					className={cn(
						'text-sm break-words',
						outcome === 'failed' && 'text-destructive',
						(outcome === 'empty' || outcome === 'partial') && 'text-amber-800 dark:text-amber-300',
					)}
				>
					{summary}
				</p>
			) : null}

			{details.length > 0 ? (
				<ul className='text-xs space-y-1 rounded-lg border border-border/60 bg-muted/20 p-2 font-mono'>
					{details.map((line, index) => (
						<li key={`${index}-${line}`} className='break-all'>
							{line}
						</li>
					))}
				</ul>
			) : null}

			{job.error && !summary?.includes(job.error) ? (
				<p className='text-xs text-destructive break-all'>{job.error}</p>
			) : null}

			<div className='text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 pt-0.5 border-t border-border/60'>
				<span>Создан: {formatDateTime(job.createdAt)}</span>
				{job.startedAt ? <span>Старт: {formatDateTime(job.startedAt)}</span> : null}
				{job.finishedAt ? <span>Завершён: {formatDateTime(job.finishedAt)}</span> : null}
				{duration != null ? <span>Длительность: {formatDuration(duration)}</span> : null}
				{job.proxyCount > 0 ? <span>{job.proxyCount} прокси</span> : null}
				{job.attempts > 0 ? <span>попыток: {job.attempts}</span> : null}
				{job.workerId ? <span>worker: {job.workerId}</span> : null}
				{job.reclaimCount > 0 ? <span>reclaim: {job.reclaimCount}</span> : null}
			</div>
		</div>
	);
}
